<template>
  <section
    v-if="offers?.length"
    class="skin-offers"
  >
    <h2>{{ $t("Active offers") }}</h2>
    <div class="skin-offers_wrap">
      <div class="skin-offers_list g250 gap32">
        <OffersCard
          v-for="item of visibleOffers"
          :key="`${item.market_name}-${item.price}`"
          :ref="
            (el) => {
              if (item.market_name === highlightedOffer?.market_name) highlightedCard.value = el;
            }
          "
          :highlighted="highlightedOffer?.market_name === item.market_name"
          :is-top="item.market_name === offers[0].market_name"
          :markets="markets"
          :name="name"
          :offer="item"
        />
      </div>
      <PaginateV2
        v-if="offers?.length > perPage && !isMobile"
        v-model="page"
        :pages-length="Math.ceil(offers?.length / perPage)"
        @update:model-value="onPageChange"
      />
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { MarketInfo, MarketOffer } from '~/graphql/generated/schema-types';
import { computed, nextTick, ref, watchEffect } from 'vue';
import OffersCard from '~/components/Pages/Item/Components/OffersCard.vue';

const props = defineProps<{
  offers: MarketOffer[]
  markets: MarketInfo[]
  name: string
}>();

const route = useRoute();
const currW = useResize();

const highlightedCard = ref(null);
const perPage = ref(6);
const page = ref(route.query.offers === 'r' ? Math.ceil(props.offers?.length / perPage.value) : 1);
const pageChanged = ref(false);

const isMobile = computed(() => currW.value <= 480);

const highlightedOffer = computed(() => {
  if (route.query.highlighted === undefined) {
    return null;
  }
  return props.offers.find(offer => offer.market_name === route.query.highlighted);
});

const visibleOffers = computed(() => {
  if (isMobile.value) {
    return props.offers;
  }
  const from = (page.value - 1) * perPage.value;
  const to = page.value * perPage.value;
  return props.offers.slice(from, to);
});

watchEffect(() => {
  if (!pageChanged.value) {
    const highlightedIndex = props.offers.findIndex(
      offer => offer.market_name === highlightedOffer.value?.market_name,
    );
    if (highlightedIndex !== -1) {
      page.value = Math.floor(highlightedIndex / perPage.value) + 1;
    }
  }
});

function onPageChange(newPage: number) {
  page.value = newPage;
  pageChanged.value = true;
}

watch([highlightedCard, isMobile], async ([card, mobile]) => {
  if (card && mobile) {
    await nextTick();

    let cardEl = highlightedCard.value;
    if (Array.isArray(cardEl))
      cardEl = cardEl[0];
    if (cardEl && '$el' in cardEl)
      cardEl = cardEl.$el;
    if (cardEl && typeof cardEl.scrollIntoView === 'function') {
      cardEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }
});
</script>

<style lang="scss" scoped>
.paginate {
  padding: 3.2rem 0 0;

  &_wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.8rem;
  }

  button {
    width: 4rem;
    height: 4rem;
    padding: 0;

    &.active {
      background: var(--accent-secondary);
      color: var(--accent-on-accent-secondary);
    }
  }
}

.skin-offers_list {
  scroll-behavior: smooth;
}

@media screen and (max-width: 480px) {
  .skin-offers {
    padding-bottom: 10px;
    overflow: hidden;
    margin: 3.2rem -1.6rem !important;

    h2 {
      margin-left: 1.6rem;
      margin-right: 1.6rem;
    }

    &_list {
      display: flex;
      gap: 1.6rem;
      overflow-x: auto;
      padding: 0 1.6rem 1rem;
    }
  }
  .paginate {
    display: none;
  }
}
</style>
